import { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { motion, AnimatePresence } from 'framer-motion'
import { X } from 'lucide-react'
import { Button } from '@/components/ui/Button'
import { LocalizedLink } from '@/components/LocalizedLink'

const COOKIE_CONSENT_KEY = 'bfwellness_cookie_consent'

export function CookieBanner() {
  const [isVisible, setIsVisible] = useState(false)
  const { t } = useTranslation()

  useEffect(() => {
    const consent = localStorage.getItem(COOKIE_CONSENT_KEY)
    if (!consent) {
      const timer = setTimeout(() => setIsVisible(true), 1000)
      return () => clearTimeout(timer)
    }
  }, [])

  const handleAccept = () => {
    localStorage.setItem(COOKIE_CONSENT_KEY, 'accepted')
    setIsVisible(false)
  }

  const handleDecline = () => {
    localStorage.setItem(COOKIE_CONSENT_KEY, 'declined')
    setIsVisible(false)
  }

  return (
    <AnimatePresence>
      {isVisible && (
        <motion.div
          initial={{ x: -100, opacity: 0 }}
          animate={{ x: 0, opacity: 1 }}
          exit={{ x: -100, opacity: 0 }}
          transition={{ duration: 0.3 }}
          className="fixed bottom-3 left-3 right-3 sm:right-auto sm:left-4 sm:bottom-4 z-modal max-w-sm bg-white rounded-xl sm:rounded-2xl border border-gray-200 shadow-xl"
        >
          <div className="p-4 sm:p-5">
            {/* Close button */}
            <button
              onClick={handleDecline}
              className="absolute top-3 right-3 p-1 text-gray-400 hover:text-gray-600 transition-colors"
              aria-label={t('aria.close')}
            >
              <X className="h-4 w-4" />
            </button>

            {/* Message */}
            <p className="text-sm text-gray-600 mb-4 leading-relaxed">
              {t('cookie.message')}{' '}
              <LocalizedLink
                to="cookiePolicy"
                className="text-accent-blue hover:underline"
              >
                {t('cookie.learnMore')}
              </LocalizedLink>
            </p>

            {/* Buttons */}
            <div className="flex items-center gap-2">
              <Button
                variant="ghost"
                size="sm"
                onClick={handleDecline}
                className="flex-1"
              >
                {t('buttons.decline')}
              </Button>
              <Button
                size="sm"
                onClick={handleAccept}
                className="flex-1"
              >
                {t('buttons.accept')}
              </Button>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  )
}
